import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SOURCES } from "@/lib/constants";
import { useExecutives } from "@/context/ExecutivesContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = {
  customer_name: "",
  event_date: "",
  contact_no: "",
  pax_count: "",
  source: "",
  remarks: "",
  assigned_executive: "",
  follow_up_date: "",
};

export default function LeadFormDialog({ open, onOpenChange, lead, onSaved }) {
  const { user } = useAuth();
  const { executives } = useExecutives();
  const isExecutive = user.role === "executive";
  const editing = Boolean(lead);
  const assignLocked = isExecutive || (editing && user.role === "sales_manager");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (lead) {
        setForm({
          customer_name: lead.customer_name,
          event_date: lead.event_date,
          contact_no: lead.contact_no,
          pax_count: String(lead.pax_count),
          source: lead.source,
          remarks: lead.remarks || "",
          assigned_executive: lead.assigned_executive,
          follow_up_date: lead.follow_up_date || "",
        });
      } else {
        setForm({
          ...EMPTY,
          assigned_executive: isExecutive ? user.username : "",
        });
      }
    }
  }, [open, lead, isExecutive, user.username]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        pax_count: parseInt(form.pax_count, 10),
      };
      if (isExecutive) payload.assigned_executive = user.username;
      if (assignLocked && !isExecutive) delete payload.assigned_executive;
      if (editing) {
        const { data } = await api.put(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/leads", payload);
        toast.success("Lead created successfully");
        onSaved(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editing ? "Edit Lead" : "New Lead"}
          </DialogTitle>
          <DialogDescription>
            Enter the lead enquiry details. Fields marked * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label>Customer Name *</Label>
            <Input
              required
              data-testid="lead-customer-name-input"
              value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value)}
              placeholder="e.g., Rajesh Sharma / TechCorp Pvt Ltd"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date of Event *</Label>
              <Input
                type="date"
                required
                data-testid="lead-event-date-input"
                value={form.event_date}
                onChange={(e) => set("event_date", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>No. of Paxs *</Label>
              <Input
                type="number"
                min="1"
                required
                data-testid="lead-pax-input"
                value={form.pax_count}
                onChange={(e) => set("pax_count", e.target.value)}
                placeholder="e.g., 250"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Contact No. *</Label>
            <Input
              required
              data-testid="lead-contact-input"
              value={form.contact_no}
              onChange={(e) => set("contact_no", e.target.value)}
              placeholder="e.g., +91 98765 43210"
            />
          </div>

          <div className="space-y-2">
            <Label>Source *</Label>
            <Select value={form.source} onValueChange={(v) => set("source", v)} required>
              <SelectTrigger data-testid="lead-source-select">
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Assigned Sales Executive *</Label>
            {assignLocked ? (
              <Input
                disabled
                data-testid="lead-assigned-locked"
                value={isExecutive ? user.name : executives.find((ex) => ex.username === form.assigned_executive)?.name || form.assigned_executive}
                className="bg-slate-100"
              />
            ) : (
              <Select
                value={form.assigned_executive}
                onValueChange={(v) => set("assigned_executive", v)}
                required
              >
                <SelectTrigger data-testid="lead-assigned-select">
                  <SelectValue placeholder="Assign to executive" />
                </SelectTrigger>
                <SelectContent>
                  {executives.map((ex) => (
                    <SelectItem key={ex.username} value={ex.username}>
                      {ex.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label>Call-back / Follow-up Date</Label>
            <Input
              type="date"
              data-testid="lead-followup-date-input"
              value={form.follow_up_date}
              onChange={(e) => set("follow_up_date", e.target.value)}
            />
            <p className="text-xs text-slate-400">
              Optional. Sets a reminder in the follow-up digest.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Remarks</Label>
            <Textarea
              data-testid="lead-remarks-input"
              value={form.remarks}
              onChange={(e) => set("remarks", e.target.value)}
              placeholder="Special dietary requests, hall preferences, stage setup..."
              rows={3}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="lead-form-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="lead-form-submit"
              className="bg-[#1E3A8A] hover:bg-[#172554]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Create Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
